import { Link } from 'react-router-dom';
import { ScanText } from 'lucide-react';

const EFFECTIVE_DATE = 'May 16, 2025';
const CONTACT_EMAIL = 'support@tyosolution.com';
const SITE_URL = 'https://ocr.tyosolution.com';

function Section({ title, children }) {
  return (
    <div className='mb-8'>
      <h2 className='text-base font-heading font-semibold text-foreground mb-3'>{title}</h2>
      <div className='text-sm text-muted-foreground leading-relaxed space-y-3'>{children}</div>
    </div>
  );
}

export default function TermsPage() {
  return (
    <div className='min-h-screen bg-background'>
      {/* Minimal header */}
      <header className='sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b border-border'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between'>
          <Link to='/' className='flex items-center gap-2'>
            <div className='w-8 h-8 rounded-lg bg-primary flex items-center justify-center'>
              <ScanText className='w-4.5 h-4.5 text-primary-foreground' strokeWidth={2.2} />
            </div>
            <span className='font-heading font-bold text-lg text-foreground'>
              Invoice<span className='text-primary'>Reader</span>
            </span>
          </Link>
          <Link
            to='/'
            className='text-sm text-muted-foreground hover:text-foreground transition-colors'
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className='max-w-3xl mx-auto px-4 sm:px-6 py-12'>
        <h1 className='text-3xl font-heading font-bold text-foreground mb-1'>Terms of Service</h1>
        <p className='text-sm text-muted-foreground mb-8'>
          Effective date: {EFFECTIVE_DATE} &nbsp;·&nbsp;{' '}
          <a href={SITE_URL} className='text-primary hover:underline'>
            {SITE_URL}
          </a>
        </p>

        <Section title='1. Acceptance of Terms'>
          <p>
            By accessing or using InvoiceReader at <strong className='text-foreground'>{SITE_URL}</strong>, you agree to be bound by these Terms of Service and our{' '}
            <Link to='/privacy' className='text-primary hover:underline'>Privacy Policy</Link>.
            If you do not agree, please do not use the service.
          </p>
        </Section>

        <Section title='2. Description of Service'>
          <p>InvoiceReader provides an OCR-based invoice field extraction API. Users upload invoice documents (images or PDFs) and receive structured extracted data such as invoice number, date, items, totals, and supplier information.</p>
          <p>The service is provided for business and personal use. Extracted data accuracy depends on the quality of the uploaded document and is not guaranteed to be 100% correct.</p>
        </Section>

        <Section title='3. Account Registration'>
          <p>To use InvoiceReader, you must register with a valid email address and verify it. You are responsible for maintaining the confidentiality of your credentials.</p>
          <p>You agree to provide accurate, complete information and to keep it up to date. You may not use another person's account or share your credentials.</p>
          <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
        </Section>

        <Section title='4. Credits and Payments'>
          <p><strong className='text-foreground'>Free credits:</strong> New accounts receive a number of free API credits upon registration for evaluation purposes.</p>
          <p><strong className='text-foreground'>Paid credit packs:</strong> Additional credits may be purchased as one-time packs (Starter, Pro, Business). Prices are displayed in USD and processed by Paddle, our authorised reseller.</p>
          <p><strong className='text-foreground'>Credit consumption:</strong> Each successful API call that produces an extraction result consumes credits according to the published rate.</p>
          <p><strong className='text-foreground'>Refund policy:</strong> Credit pack purchases are eligible for a full refund within 14 days of the transaction date. To request a refund, contact us at <a href='mailto:support@tyosolution.com' className='text-primary hover:underline'>support@tyosolution.com</a>. Refunds are processed by Paddle, our authorised reseller, in accordance with <a href='https://www.paddle.com/legal/refunds' className='text-primary hover:underline' target='_blank' rel='noreferrer'>Paddle's refund policy</a>.</p>
          <p><strong className='text-foreground'>No subscription:</strong> All purchases are one-time; there are no recurring charges.</p>
        </Section>

        <Section title='5. Acceptable Use'>
          <p>You agree not to use InvoiceReader to:</p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>Upload content that is unlawful, harmful, or infringes any third-party rights</li>
            <li>Attempt to gain unauthorized access to any part of the service or its infrastructure</li>
            <li>Automate bulk uploads beyond the intended API rate limits</li>
            <li>Reverse-engineer, decompile, or copy the OCR or extraction algorithms</li>
            <li>Use the service to build a directly competing OCR product without prior written consent</li>
            <li>Interfere with or disrupt the service or its servers</li>
          </ul>
        </Section>

        <Section title='6. API Usage'>
          <p>Access to the InvoiceReader API is subject to rate limits as documented in the API reference. Repeated abuse of rate limits may result in account suspension.</p>
          <p>API keys and JWT tokens must be kept confidential. You are responsible for all activity that occurs under your account.</p>
        </Section>

        <Section title='7. Intellectual Property'>
          <p>The InvoiceReader platform, software, and OCR pipeline are owned by TYO Solution and protected by applicable intellectual property laws.</p>
          <p>You retain ownership of the documents you upload. By uploading them, you grant us a temporary, limited license to process them solely for generating extraction results.</p>
        </Section>

        <Section title='8. Disclaimer of Warranties'>
          <p>The service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied. We do not warrant that extraction results will be 100% accurate or error-free.</p>
        </Section>

        <Section title='9. Limitation of Liability'>
          <p>To the maximum extent permitted by applicable law, TYO Solution shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of InvoiceReader.</p>
          <p>Our total liability shall not exceed the amount you paid to us in the 30 days prior to the claim.</p>
        </Section>

        <Section title='10. Termination'>
          <p>We may suspend or terminate your account at our discretion if you violate these Terms. Upon termination, any unused credits may be forfeited.</p>
          <p>You may delete your account at any time by contacting <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline'>{CONTACT_EMAIL}</a>.</p>
        </Section>

        <Section title='11. Governing Law'>
          <p>These Terms shall be governed by and construed in accordance with the laws of the Republic of Indonesia.</p>
        </Section>

        <Section title='12. Changes to Terms'>
          <p>We reserve the right to modify these Terms at any time. We will notify you of significant changes by updating the effective date. Continued use of the service after changes constitutes your acceptance.</p>
        </Section>

        <Section title='13. Contact'>
          <p>
            For questions about these Terms, contact us at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className='text-primary hover:underline font-medium'>
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className='text-foreground/70'>TYO Solution &nbsp;·&nbsp; <a href='https://tyosolution.com' className='text-primary hover:underline'>tyosolution.com</a></p>
        </Section>

        <div className='border-t border-border pt-6 mt-4 flex gap-4 text-sm'>
          <Link to='/privacy' className='text-primary hover:underline'>Privacy Policy</Link>
          <Link to='/' className='text-muted-foreground hover:text-foreground'>← Back to Home</Link>
        </div>
      </main>
    </div>
  );
}
